import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { icons } from '../ui/Icons';

interface ProtectedRouteProps {
  requiredPermission?: string;
  requiredPermissions?: string[];
  children?: React.ReactNode;
}

export function ProtectedRoute({ requiredPermission, requiredPermissions, children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasPermission } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-950">
        <div className="flex items-center gap-3 text-sm font-medium text-neutral-600 dark:text-neutral-300">
          <icons.Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
          Anmeldung wird geprüft...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const permissionAllowed = requiredPermission
    ? hasPermission(requiredPermission)
    : requiredPermissions?.some(hasPermission) ?? true;

  if (!permissionAllowed) {
    const requiredLabel = requiredPermission || requiredPermissions?.join(', ') || 'Berechtigung';
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-neutral-50 px-4 dark:bg-neutral-950">
        <div className="w-full max-w-md text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-md bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300">
            <icons.Lock className="h-6 w-6" aria-hidden="true" />
          </div>
          <h1 className="mt-5 text-h2 font-semibold text-neutral-900 dark:text-neutral-100">Zugriff verweigert</h1>
          <p className="mt-2 text-body text-neutral-600 dark:text-neutral-300">
            Für diesen Bereich ist eine zusätzliche Berechtigung erforderlich.
          </p>
          <p className="mt-3 rounded-md bg-white px-3 py-2 font-mono text-xs text-neutral-600 border border-neutral-200 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300">
            {requiredLabel}
          </p>
          <button
            type="button"
            onClick={() => window.history.back()}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:bg-primary-500 dark:hover:bg-primary-600"
          >
            <icons.ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Zurück
          </button>
        </div>
      </div>
    );
  }

  return children || <Outlet />;
}
